<template>
	<br>
</template>
<script>
import { getSearchResultsApi } from '../../../../../shared/utils/api/data-subject-search.js'
import { DATA_SUBJECT, DATA_SUBJECT_SEARCH } from '../../../router/route-names.js'
import { csPortalBrandSelector, userBrandIdsFilter, UNIVERSAL_BRAND, changeSelectedBrand } from '../../../../../shared/state/brands.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { VALID_SEARCH_FIELDS } from '../../../../../shared/lib/magic-link-valid-fields.js'

/**
 * Multi-parameter data subject search component.
 * Handles direct navigation via URL query parameters.
 * Example: /search?urn1=ABC123&custom3=butler&brandId=1
 * Ticket: #23707
 */
export default {
	name: 'multi-data-subject-search',
	props: {
		searchParams: {
			type: Object,
			required: true
		},
		brandId: {
			type: String,
			required: true
		}
	},
	setup () {
		return {
			csPortalBrandSelector,
			userBrandIdsFilter,
			changeSelectedBrand
		}
	},
	created () {
		this.trySearch()
	},
	methods: {
		async trySearch () {
			const searchBrandId = Number.parseInt(this.brandId, 10)

			// Validate brand ID is a valid number
			if (Number.isNaN(searchBrandId)) {
				return this.goToSearchPage()
			}

			// Validate we have at least one search parameter
			if (!this.searchParams || Object.keys(this.searchParams).length === 0) {
				showSnackbar({ text: 'No search fields provided', color: 'red' })
				return this.goToSearchPage()
			}

			// Validate search parameters against allowed fields
			const invalidFields = Object.values(this.searchParams)
				.filter(({ key }) => !VALID_SEARCH_FIELDS.has(key))
				.map(({ key }) => key)
			if (invalidFields.length > 0) {
				showSnackbar({ text: `Invalid search fields: ${invalidFields.join(', ')}`, color: 'red' })
				return this.goToSearchPage()
			}

			// Validate search parameters for having non-empty values
			const emptyFields = Object.values(this.searchParams)
				.filter(({ value }) => !value || value.trim().length === 0)
				.map(({ key }) => key)
			if (emptyFields.length > 0) {
				showSnackbar({ text: `Empty search values for fields: ${emptyFields.join(', ')}`, color: 'red' })
				return this.goToSearchPage()
			}

			// Validate search parameters for not having duplicate fields
			const fieldSet = new Set()
			const duplicateFieldsSet = new Set()
			for (const [, { key }] of Object.entries(this.searchParams)) {
				if (fieldSet.has(key)) {
					duplicateFieldsSet.add(key)
				} else {
					fieldSet.add(key)
				}
			}
			if (duplicateFieldsSet.size > 0) {
				showSnackbar({ text: `Duplicate search fields: ${[...duplicateFieldsSet].join(', ')}`, color: 'red' })
				return this.goToSearchPage()
			}

			// Force brand selector evaluation (kept from original logic)
			// eslint-disable-next-line no-unused-vars
			const _ = csPortalBrandSelector.value

			// Brand access validation - ensure user has access to the specified brand
			if (searchBrandId !== UNIVERSAL_BRAND && !userBrandIdsFilter.value.includes(searchBrandId) && !userBrandIdsFilter.value.includes(UNIVERSAL_BRAND)) {
				return this.goToSearchPage()
			}

			changeSelectedBrand(searchBrandId)

			const displayDataSubjectsWithNoPreferences = true

			// Flatten search parameters to field:value pairs
			const searchFields = Object.values(this.searchParams).reduce((acc, { key, value }) => {
				acc[key] = value
				return acc
			}, {})

			try {
				const { data = [] } = await getSearchResultsApi(
					searchFields,
					searchBrandId,
					displayDataSubjectsWithNoPreferences,
					true
				)

				// Only navigate to data subject if exactly one result
				if (data.length === 1) {
					const row = data[0]
					const allIds = data
						.filter(({ groupId, syrenisId }) =>
							groupId !== null ? groupId === row.groupId : syrenisId === row.syrenisId
						)
						.map(({ syrenisId }) => syrenisId)
						.join(',')

					return this.$router.push({
						name: DATA_SUBJECT,
						query: {
							id: row.syrenisId,
							allIds,
							brandId: searchBrandId
						}
					})
				}

				// Zero or multiple results: return to search page
				this.goToSearchPage()
			} catch {
				// API error: return to search page
				this.goToSearchPage()
			}
		},
		goToSearchPage () {
			this.$router.replace({ name: DATA_SUBJECT_SEARCH })
		}
	}
}
</script>
